import { DEFAULT_VOLUMES, type Volumes } from "../../audio/audio-model.ts";
import { BELT_KEYS, KEYS } from "../../game/player-input.ts";
import { POST_PRESETS } from "../../render/post/post-model.ts";

/**
 * Configurações do jogador (doc 05 §Configurações e §HUD e telas; doc 06 §Preferências; roadmap
 * F30): tudo num só JSON versionado no navegador (`eldrun:opcoes`), com migração das chaves que
 * vieram antes (`eldrun:volumes`, `eldrun:legendas`). O tutorial guarda o próprio progresso (F28).
 */
export type Action = keyof typeof KEYS;
export type Bindings = { [A in Action]: string[] };
export type GraphicsPreset = "baixo" | "medio" | "alto" | "automatico";

export interface Graphics {
  preset: GraphicsPreset;
  /** Escala de resolução (0,5 a 1,0), a opção de maior impacto. */
  resolution: number;
  /** Densidade do capim e dos enfeites do chão ("Capim e detalhes", F44): 0, 50 ou 100 %. */
  grass: 0 | 50 | 100;
  /** Distância de visão (fator sobre o padrão): recursos, criaturas, itens no chão e o relevo. */
  distance: number;
  /**
   * Pós-processamento (F52): "Brilho (bloom)", "Correção de cor" e "Vinheta". Valem no Médio e no
   * Alto (o Baixo desenha direto, sem compositor); o bloom também (F61; no Médio, com menos níveis de
   * borrão). O "Contorno" saiu na Fase 15.
   */
  bloom: boolean;
  grade: boolean;
  vignette: boolean;
  /** Fase 13 (G3): "Sombras" — a sombra do sol (mapa de 1024 no Médio, 2048 no Alto); desligada, as sombras-blob. */
  shadows: boolean;
  /** Fase 13 (G5): "Luz cinematográfica", "Aberração cromática", "Reflexo de lente" (o "Desfoque de distância" saiu na Fase 15). */
  light: boolean;
  aberration: boolean;
  flare: boolean;
  /** Fase 14: "Desfoque de movimento" (ligado só no Alto). */
  motion: boolean;
}

export interface Settings {
  v: 1;
  /** FOV vertical da câmera em terceira pessoa (60 a 100; correr abre e mirar fecha por cima, F55). */
  fov: number;
  /** Multiplicador da sensibilidade do mouse (0,25 a 3). */
  sensitivity: number;
  invertX: boolean;
  invertY: boolean;
  /** Tremor de câmera, 0 a 100 % ("Reduzir lampejos" corta à metade). */
  shake: number;
  bindings: Bindings;
  graphics: Graphics;
  volumes: Volumes;
  captions: boolean;
  reduceFlashes: boolean;
  colorblind: boolean;
  /** Efeitos dos aliados (F68, doc 09 §2.5): força dos estilhaços e zonas dos outros, em %. */
  allyFx: AllyFx;
  /** Painel de FPS e desempenho na partida; sem escolha guardada, ligado só no desenvolvimento. */
  showPerf?: boolean;
}

export type AllyFx = 100 | 60 | 30;

export const SETTINGS_PREF = "opcoes";
export const LIMITS = { fov: [60, 100], sensitivity: [0.25, 3], resolution: [0.5, 1], distance: [0.6, 1.4] } as const;

/**
 * Cada preset (doc 05: escala, capim, distância; F52: o pós-processamento). Médio é o padrão (doc 05,
 * risco 1): até a F51, igual ao jogo antes da F30; desde a F52, com correção de cor e vinheta (o
 * visual padrão); desde a F61, também o bloom (no Médio com menos níveis). O Baixo fica sem pós.
 */
export const PRESETS: Record<Exclude<GraphicsPreset, "automatico">, Omit<Graphics, "preset">> = {
  baixo: { resolution: 0.7, grass: 50, distance: 0.7, shadows: false, ...POST_PRESETS.baixo },
  medio: { resolution: 1, grass: 100, distance: 1, shadows: true, ...POST_PRESETS.medio },
  alto: { resolution: 1, grass: 100, distance: 1.3, shadows: true, ...POST_PRESETS.alto },
};

/**
 * Teto do pixel ratio por tier (Fase 12, Gate D): numa tela Retina (DPR 2) o Médio desenhava 4× os
 * pixels do 1080p em DPR 1 (8,3 M contra 2,1 M), antes do pós. O Baixo fica em 1, o Médio em 1,5 e o
 * Alto em 2; a escala de resolução do preset (0,7 no Baixo) entra por cima. O HUD é DOM e continua nítido.
 */
export const DPR_CAP: Readonly<Record<Exclude<GraphicsPreset, "automatico">, number>> = { baixo: 1, medio: 1.5, alto: 2 };

/** O pixel ratio do canvas: o do aparelho, no teto do tier, vezes a escala de resolução. */
export function pixelRatio(device: number, tier: Exclude<GraphicsPreset, "automatico">, resolution: number): number {
  return Math.min(device > 0 ? device : 1, DPR_CAP[tier]) * resolution;
}

export const defaultBindings = (): Bindings => Object.fromEntries(Object.entries(KEYS).map(([a, keys]) => [a, [...keys]])) as Bindings;

export function defaultSettings(): Settings {
  return {
    v: 1,
    fov: 70,
    sensitivity: 1,
    invertX: false,
    invertY: false,
    shake: 100,
    bindings: defaultBindings(),
    graphics: { preset: "medio", ...PRESETS.medio },
    volumes: { ...DEFAULT_VOLUMES },
    captions: false,
    reduceFlashes: false,
    colorblind: false,
    allyFx: 60,
  };
}

const clamp = (v: unknown, [lo, hi]: readonly [number, number], fallback: number): number => (typeof v === "number" && Number.isFinite(v) ? Math.max(lo, Math.min(hi, v)) : fallback);
const bool = (v: unknown, fallback: boolean): boolean => (typeof v === "boolean" ? v : fallback);
const CODE = /^(Key[A-Z]|Digit[09]|Numpad([0-9]|Add|Subtract|Multiply|Divide|Decimal|Enter)|Arrow(Up|Down|Left|Right)|F([1-9]|1[0-2])|Shift(Left|Right)|Control(Left|Right)|Alt(Left|Right)|Space|Enter|Tab|Backquote|Minus|Equal|Bracket(Left|Right)|Backslash|Semicolon|Quote|Comma|Period|Slash|IntlRo|IntlBackslash|CapsLock|MouseMiddle|MouseRight|Backspace|Delete|Insert|Home|End|PageUp|PageDown)$/;
const BELT = new Set<string>(BELT_KEYS);

/**
 * Tecla que dá para usar numa ação: a mesma regra na captura e ao ler o guardado. 1–8 são do cinto,
 * Esc solta o ponteiro (o navegador não entrega), e teclas sem nome conhecido ficam de fora.
 */
export function bindableKey(code: string): boolean {
  return CODE.test(code) && !BELT.has(code);
}

/**
 * Teclas guardadas válidas, sem a mesma tecla em duas ações (vale a primeira guardada); ação sem
 * tecla válida volta ao padrão, se o padrão estiver livre. Ação nova (que o guardado não conhece, como a
 * ficha da F82) toma a tecla padrão dela de quem ainda fica com outra: o C saiu do agachar (Ctrl ou C).
 */
function parseBindings(raw: unknown): Bindings {
  const defaults = defaultBindings();
  const out = defaultBindings();
  const used = new Set<string>();
  const stored = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const claimed = new Set((Object.keys(out) as Action[]).filter((a) => !(a in stored)).flatMap((a) => defaults[a]));
  const missing: Action[] = [];
  for (const action of Object.keys(out) as Action[]) {
    const keys = stored[action];
    let valid = Array.isArray(keys) ? [...new Set(keys.filter((k): k is string => typeof k === "string" && bindableKey(k)))].filter((k) => !used.has(k)).slice(0, 2) : [];
    const kept = valid.filter((k) => !claimed.has(k));
    if (kept.length > 0) valid = kept;
    if (valid.length) {
      out[action] = valid;
      for (const k of valid) used.add(k);
    } else missing.push(action);
  }
  for (const action of missing) {
    out[action] = defaults[action].filter((k) => !used.has(k));
    for (const k of out[action]) used.add(k);
  }
  return out;
}

/**
 * Lê o guardado. Sem `eldrun:opcoes`, aproveita as chaves antigas (volumes e legendas); versão
 * diferente ou valor inválido volta ao padrão, campo por campo. As opções do pós (F52) que faltam
 * (guardado de antes da F52) vêm do preset guardado.
 */
export function parseSettings(raw: string | null | undefined, legacy: { volumes?: string | null; captions?: string | null } = {}): Settings {
  const d = defaultSettings();
  let data: Partial<Settings> & Record<string, unknown> = {};
  try {
    data = JSON.parse(raw ?? "") as typeof data;
    if (data.v !== 1) data = {};
  } catch {
    data = {};
  }
  if (!raw) {
    // Migração (F29): volumes e legendas guardados antes da tela de opções.
    try {
      if (legacy.volumes) data.volumes = JSON.parse(legacy.volumes) as Volumes;
    } catch {
      // volume inválido: fica o padrão
    }
    if (legacy.captions === "1") data.captions = true;
  }
  const g = (data.graphics ?? {}) as Partial<Graphics>;
  const preset: GraphicsPreset = g.preset === "baixo" || g.preset === "alto" || g.preset === "automatico" || g.preset === "medio" ? g.preset : "medio";
  const post = PRESETS[preset === "automatico" ? "medio" : preset];
  const v = (data.volumes ?? {}) as Partial<Volumes>;
  const vol = (k: keyof Volumes) => clamp(v[k], [0, 1], d.volumes[k]);
  return {
    v: 1,
    // Guardado antes da F55 (tem `camera`): o FOV era o da primeira pessoa, e a terceira usava 5° a
    // menos; agora a opção é a da terceira, então volta o que se via nela. `camera` e `headBob` saem.
    fov: Math.round(clamp(typeof data.fov === "number" && "camera" in data ? data.fov - 5 : data.fov, LIMITS.fov, d.fov)),
    sensitivity: clamp(data.sensitivity, LIMITS.sensitivity, d.sensitivity),
    invertX: bool(data.invertX, false),
    invertY: bool(data.invertY, false),
    shake: Math.round(clamp(data.shake, [0, 100], d.shake)),
    bindings: parseBindings(data.bindings),
    graphics: {
      preset,
      resolution: clamp(g.resolution, LIMITS.resolution, d.graphics.resolution),
      grass: g.grass === 0 || g.grass === 50 ? g.grass : 100,
      distance: clamp(g.distance, LIMITS.distance, d.graphics.distance),
      bloom: bool(g.bloom, post.bloom),
      grade: bool(g.grade, post.grade),
      vignette: bool(g.vignette, post.vignette),
      shadows: bool(g.shadows, PRESETS[preset === "automatico" ? "medio" : preset].shadows),
      light: bool(g.light, post.light),
      aberration: bool(g.aberration, post.aberration),
      flare: bool(g.flare, post.flare),
      motion: bool(g.motion, post.motion),
    },
    volumes: { geral: vol("geral"), musica: vol("musica"), efeitos: vol("efeitos"), ambiente: vol("ambiente"), ui: vol("ui") },
    captions: bool(data.captions, false),
    reduceFlashes: bool(data.reduceFlashes, false),
    colorblind: bool(data.colorblind, false),
    allyFx: data.allyFx === 100 || data.allyFx === 30 ? data.allyFx : 60,
    ...(typeof data.showPerf === "boolean" && { showPerf: data.showPerf }),
  };
}

/**
 * Mede o tempo de quadro do Automático: 10 s de quadros ativos (aba à vista, sem a tela de Opções),
 * desde que o Automático passou a valer; depois, a média escolhe o preset.
 */
export class AutoMeter {
  private sum = 0;
  private frames = 0;

  reset(): void {
    this.sum = 0;
    this.frames = 0;
  }

  /** Um quadro (ms); devolve o preset quando já mediu 10 s. */
  push(ms: number, active: boolean): Exclude<GraphicsPreset, "automatico"> | undefined {
    if (!active || !(ms > 0) || ms > 1000) return undefined;
    this.sum += ms;
    this.frames++;
    return this.sum >= 10_000 ? autoPreset(this.sum / this.frames) : undefined;
  }
}

/** Troca o preset: os campos dele entram (o Automático parte do Médio até medir). */
export function withPreset(g: Graphics, preset: GraphicsPreset): Graphics {
  return { preset, ...PRESETS[preset === "automatico" ? "medio" : preset] };
}

/**
 * Ajuste à mão de uma opção de Gráficos (resolução, capim, distância, as do pós). No Automático, sai
 * dele para o preset que estava valendo, com os campos desse preset: o guardado do Automático tem os
 * do Médio, e partir dele desligava o bloom e encurtava a distância de quem o Automático pôs no Alto.
 */
export function manualGraphics(g: Graphics, effective: Exclude<GraphicsPreset, "automatico">, patch: Partial<Graphics>): Graphics {
  return { ...(g.preset === "automatico" ? withPreset(g, effective) : g), ...patch };
}

/**
 * Remapeia: a tecla passa a ser a única da ação; quem já a usava perde só ela (e, se ficar sem
 * nenhuma, recebe a tecla antiga da ação remapeada: troca). Devolve as teclas e quem foi afetado.
 */
export function rebind(b: Bindings, action: Action, code: string): { bindings: Bindings; moved?: Action } {
  const next = Object.fromEntries(Object.entries(b).map(([a, k]) => [a, [...k]])) as Bindings;
  const old = next[action][0];
  let moved: Action | undefined;
  for (const a of Object.keys(next) as Action[]) {
    if (a === action || !next[a].includes(code)) continue;
    next[a] = next[a].filter((k) => k !== code);
    if (next[a].length === 0 && old) next[a] = [old];
    // Sem tecla antiga para trocar (a ação remapeada não tinha nenhuma, F82): a padrão dela, se estiver livre.
    if (next[a].length === 0) {
      const free = KEYS[a].filter((k) => k !== code && !Object.values(next).some((keys) => keys.includes(k)));
      if (free.length > 0) next[a] = [free[0]!];
    }
    moved = a;
  }
  next[action] = [code];
  return moved ? { bindings: next, moved } : { bindings: next };
}

/**
 * Automático (doc 05): mede o tempo de quadro nos primeiros 10 s de jogo (no Médio, já com o pós da
 * F52) e escolhe. O Alto pede mais folga desde a F52: soma o bloom (10 desenhos a meia resolução e
 * menos) à distância a mais; as sombras do doc ainda não existem.
 */
export function autoPreset(averageMs: number): Exclude<GraphicsPreset, "automatico"> {
  if (averageMs <= 12) return "alto";
  if (averageMs <= 24) return "medio";
  return "baixo";
}

/** Nomes das ações na tela de controles. */
export const ACTION_LABELS: Record<Action, string> = {
  forward: "Andar para a frente",
  back: "Andar para trás",
  left: "Andar para a esquerda",
  right: "Andar para a direita",
  jump: "Pular",
  sprint: "Correr",
  crouch: "Agachar / Rasteira",
  inventory: "Inventário",
  discard: "Largar item",
  crafting: "Fabricação",
  rotate: "Girar peça",
  interact: "Interagir",
  dismantle: "Desmontar",
  map: "Mapa",
  ping: "Ping",
  chat: "Chat",
  settings: "Opções",
  skill: "Habilidade ativa",
  ultimate: "Habilidade suprema (ultimate)",
  abilities: "Bênçãos e Habilidades",
  sheet: "Ficha da classe",
};

/** Lampejos limitados (WCAG 2.3.1: no máximo 3 por segundo); com redução de flashes, nenhum. */
export class FlashLimiter {
  private readonly times: number[] = [];

  allow(now: number, reduce: boolean): boolean {
    if (reduce) return false;
    while (this.times.length && now - this.times[0]! >= 1000) this.times.shift();
    if (this.times.length >= 3) return false;
    this.times.push(now);
    return true;
  }
}
